import type {
  BuildingService,
  ContextSetpoint,
  DataPointSample,
  DataPointSource,
} from "@/lib/api/building";
import {
  getLatestDataPoints,
  listBuildingServices,
  listDataPointSources,
  listEquipmentContextSetpoints,
} from "@/lib/api/building";
import type { ZoneEquipmentSignal, ZoneKpis } from "@/lib/api/agent";
import { getZoneEquipmentSignals, getZoneKpisSummary } from "@/lib/api/agent";
import { labelsFromSources } from "@/lib/data-point-labels";
import { indexZoneKpisByZone } from "@/lib/zone-kpi";

/** Composed live Signaalit bag for one building — parity with DataPointsExplorer. */
export type BuildingSignaalitSnapshotBag = {
  sources: DataPointSource[];
  samples: DataPointSample[];
  behavior_labels: Record<string, string>;
  building_services: BuildingService[];
  context_setpoints_by_source: Record<string, ContextSetpoint[]>;
  zone_equipment_signals: Record<string, ZoneEquipmentSignal[]>;
  zone_kpis: Record<string, ZoneKpis>;
};

/** Injected HTTP ports for composing a Building Signaalit snapshot. */
export type BuildingSignaalitSnapshotDeps = {
  listDataPointSources: (buildingId: string) => Promise<DataPointSource[]>;
  listBuildingServices: (buildingId: string) => Promise<BuildingService[]>;
  getLatestDataPoints: (
    buildingId: string,
    sourceIds: string[],
  ) => Promise<DataPointSample[]>;
  listEquipmentContextSetpoints: (
    buildingId: string,
    equipmentId: string,
  ) => Promise<ContextSetpoint[]>;
  getZoneEquipmentSignals: (
    buildingId: string,
    zoneId: string,
  ) => Promise<{ signals: ZoneEquipmentSignal[] }>;
  getZoneKpisSummary: (
    buildingId: string,
  ) => Promise<{ zone_id: string; kpis: ZoneKpis }[]>;
};

const defaultSnapshotDeps: BuildingSignaalitSnapshotDeps = {
  listDataPointSources,
  listBuildingServices,
  getLatestDataPoints,
  listEquipmentContextSetpoints,
  getZoneEquipmentSignals,
  getZoneKpisSummary,
};

/** Injected composed fetch — system boundary for the Building Signaalit snapshot cache. */
export type FetchBuildingSignaalitSnapshot = (
  buildingId: string,
) => Promise<BuildingSignaalitSnapshotBag>;

export type BuildingSignaalitSnapshotState =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "ok";
      buildingId: string;
      bag: BuildingSignaalitSnapshotBag;
    };

export interface BuildingSignaalitSnapshotCache {
  getState: (buildingId: string) => BuildingSignaalitSnapshotState;
  /** First load or join in-flight load; no-op HTTP when that building is already ok. */
  load: (buildingId: string) => Promise<void>;
  /** Re-read a building whose data changed (its change stream said so). The current bag stays
   *  on screen until the new one arrives, and a failed re-read keeps it. */
  refresh: (buildingId: string) => Promise<void>;
  subscribe: (buildingId: string, listener: () => void) => () => void;
}

type BuildingEntry = {
  state: BuildingSignaalitSnapshotState;
  inFlight: Promise<void> | null;
  listeners: Set<() => void>;
};

export function emptyBuildingSignaalitSnapshotBag(): BuildingSignaalitSnapshotBag {
  return {
    sources: [],
    samples: [],
    behavior_labels: {},
    building_services: [],
    context_setpoints_by_source: {},
    zone_equipment_signals: {},
    zone_kpis: {},
  };
}

/**
 * Compose the live Signaalit bag for one building. Each underlying fetch soft-fails
 * to an empty collection — parity with the former DataPointsExplorer load path.
 */
export async function fetchBuildingSignaalitSnapshotBag(
  buildingId: string,
  deps: BuildingSignaalitSnapshotDeps = defaultSnapshotDeps,
): Promise<BuildingSignaalitSnapshotBag> {
  const [srcs, services] = await Promise.all([
    deps.listDataPointSources(buildingId).catch((): DataPointSource[] => []),
    deps.listBuildingServices(buildingId).catch((): BuildingService[] => []),
  ]);
  const labels = labelsFromSources(srcs);
  const source_ids = srcs.map((s) => s.source_id);

  let latest: DataPointSample[] = [];
  if (source_ids.length > 0) {
    latest = await deps
      .getLatestDataPoints(buildingId, source_ids)
      .catch((): DataPointSample[] => []);
  }

  // One context-setpoint (asetusarvo) family read per equipment source — no bulk endpoint
  // exists yet, mirroring listEquipmentContextSetpoints' per-equipment shape (story #1515).
  const equipment_ids = srcs
    .filter((s) => s.source_type === "equipment")
    .map((s) => s.source_id);
  const context_setpoint_entries = await Promise.all(
    equipment_ids.map((id): Promise<[string, ContextSetpoint[]]> =>
      deps
        .listEquipmentContextSetpoints(buildingId, id)
        .then((cs): [string, ContextSetpoint[]] => [id, cs])
        .catch((): [string, ContextSetpoint[]] => [id, []]),
    ),
  );

  const zone_ids = srcs
    .filter((s) => s.source_type === "zone")
    .map((s) => s.source_id);
  // Independent of each other (per-zone signals vs. one building-wide KPI batch) — run
  // together rather than one after the other.
  const [zone_signal_entries, zone_kpi_entries] = await Promise.all([
    Promise.all(
      zone_ids.map((id): Promise<[string, ZoneEquipmentSignal[]]> =>
        deps
          .getZoneEquipmentSignals(buildingId, id)
          .then((r): [string, ZoneEquipmentSignal[]] => [id, r.signals])
          .catch((): [string, ZoneEquipmentSignal[]] => [id, []]),
      ),
    ),
    // One batch call for every zone's resolved KPI triple (task #1761) — the same contract
    // the Sisäilmaolosuhteet card and Energiatehokkuus read (task #1766: @/lib/zone-kpi.ts).
    zone_ids.length > 0
      ? deps
          .getZoneKpisSummary(buildingId)
          .catch((): { zone_id: string; kpis: ZoneKpis }[] => [])
      : Promise.resolve<{ zone_id: string; kpis: ZoneKpis }[]>([]),
  ]);

  return {
    sources: srcs,
    samples: latest,
    behavior_labels: labels,
    building_services: services,
    context_setpoints_by_source: Object.fromEntries(
      context_setpoint_entries.filter(([, cs]) => cs.length > 0),
    ),
    zone_equipment_signals: Object.fromEntries(zone_signal_entries),
    zone_kpis: Object.fromEntries(indexZoneKpisByZone(zone_kpi_entries)),
  };
}

/**
 * Framework-agnostic in-memory cache for a building's live Signaalit snapshot.
 * React / Zustand / the change stream stay outside this module.
 */
export function createBuildingSignaalitSnapshotCache(
  fetchSnapshot: FetchBuildingSignaalitSnapshot,
): BuildingSignaalitSnapshotCache {
  const entries = new Map<string, BuildingEntry>();

  function entryFor(buildingId: string): BuildingEntry {
    let entry = entries.get(buildingId);
    if (!entry) {
      entry = {
        state: { status: "idle" },
        inFlight: null,
        listeners: new Set(),
      };
      entries.set(buildingId, entry);
    }
    return entry;
  }

  function notify(entry: BuildingEntry) {
    for (const listener of entry.listeners) listener();
  }

  function setState(
    entry: BuildingEntry,
    next: BuildingSignaalitSnapshotState,
  ) {
    entry.state = next;
    notify(entry);
  }

  function runFetch(
    buildingId: string,
    entry: BuildingEntry,
    keepCurrent = false,
  ): Promise<void> {
    if (entry.inFlight) return entry.inFlight;
    const current = entry.state;
    const refreshing = keepCurrent && current.status === "ok";
    if (!refreshing) setState(entry, { status: "loading" });
    entry.inFlight = (async () => {
      try {
        const bag = await fetchSnapshot(buildingId);
        setState(entry, { status: "ok", buildingId, bag });
      } catch {
        // Soft-fail parity: a thrown composer still settles to an empty ok bag — unless this was
        // a refresh, which keeps what is already shown.
        if (!refreshing) {
          setState(entry, {
            status: "ok",
            buildingId,
            bag: emptyBuildingSignaalitSnapshotBag(),
          });
        }
      } finally {
        entry.inFlight = null;
      }
    })();
    return entry.inFlight;
  }

  return {
    getState: (buildingId) => entryFor(buildingId).state,
    load: (buildingId) => {
      const entry = entryFor(buildingId);
      if (entry.state.status === "ok") return Promise.resolve();
      return runFetch(buildingId, entry);
    },
    refresh: (buildingId) => runFetch(buildingId, entryFor(buildingId), true),
    subscribe: (buildingId, listener) => {
      const entry = entryFor(buildingId);
      entry.listeners.add(listener);
      return () => {
        entry.listeners.delete(listener);
      };
    },
  };
}

let sharedBuildingSignaalitSnapshotCache: BuildingSignaalitSnapshotCache | null =
  null;

/** App-wide Building Signaalit snapshot cache for live explorers. */
export function getSharedBuildingSignaalitSnapshotCache(): BuildingSignaalitSnapshotCache {
  if (!sharedBuildingSignaalitSnapshotCache) {
    sharedBuildingSignaalitSnapshotCache = createBuildingSignaalitSnapshotCache(
      (buildingId) => fetchBuildingSignaalitSnapshotBag(buildingId),
    );
  }
  return sharedBuildingSignaalitSnapshotCache;
}
